import React, { useEffect, useRef, useState } from 'react';
import { printHtmlSilently } from '../lib/silentPrint';
import axios from 'axios';
import { Button } from './ui/button';
import { Printer, X, FileText } from 'lucide-react';
import { useCompanySettings } from '../hooks/useCompanySettings';
import QRCode from 'react-qr-code';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal exibindo cupom da venda (fiscal ou não-fiscal) com botão imprimir/reimprimir.
 * Para cupom fiscal: oferece emissão NFCe (modo rascunho) que gera chave SEFAZ + QR Code.
 */
export const ReceiptModal = ({ sale, onClose, autoPrint = false, silent = false }) => {
  const company = useCompanySettings();
  const printRef = useRef(null);
  const printBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [nfce, setNfce] = useState(sale?.nfce || null);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Setas ←/→ alternam foco entre Fechar e Imprimir
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const focused = document.activeElement;
        if (focused === printBtnRef.current) {
          closeBtnRef.current?.focus();
        } else {
          printBtnRef.current?.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setNfce(sale?.nfce || null);
  }, [sale]);

  // Detecta se os dados da empresa já foram carregados (evita imprimir cupom sem cabeçalho)
  const companyReady = !!(company && (company.cnpj || company.legal_name || (company.trade_name && company.trade_name !== 'Smart Flow')));

  useEffect(() => {
    if (autoPrint && !silent && sale && companyReady) {
      const t = setTimeout(() => handlePrint(), 400);
      return () => clearTimeout(t);
    }
  }, [autoPrint, silent, sale, companyReady]);

  // Modo silencioso: envia direto para a impressora (iframe oculto) e fecha sozinho — sem prévia.
  const silentDone = useRef(false);
  useEffect(() => {
    if (!silent || !sale || !companyReady || silentDone.current) return;
    silentDone.current = true;
    const paidCash = (sale.payments || []).some((p) => p.method === 'dinheiro' && Number(p.amount) > 0) || (!sale.payments?.length && sale.payment_method === 'dinheiro');
    printHtmlSilently(buildFullDocument(), { openDrawer: paidCash }).then(() => onClose());
  }, [silent, sale, companyReady]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sale) return null;

  const nfceLabel = (n) => {
    if (!n) return null;
    if (n.status === 'autorizado') return n.ambiente === 'producao' ? 'NFC-e AUTORIZADA' : 'NFC-e AUTORIZADA (HOMOLOGAÇÃO - SEM VALOR FISCAL)';
    if (n.status === 'contingencia_pendente') return 'NFC-e EMITIDA EM CONTINGENCIA OFFLINE';
    if (n.status === 'cancelado') return 'NFC-e CANCELADA';
    if (n.status === 'erro_autorizacao' || n.status === 'denegado') return 'NFC-e NAO AUTORIZADA';
    return 'NFC-e (RASCUNHO - SEM VALIDADE FISCAL)';
  };
  const nfceOk = nfce && !['erro_autorizacao', 'denegado'].includes(nfce.status);

  const handleIssueNFCe = async () => {
    if (!company.nfce_enabled) {
      alert('NFC-e não está habilitada em Configurações → Fiscal');
      return;
    }
    setIssuing(true);
    try {
      const r = await axios.post(`${API}/nfce/issue/${sale.id}`, {}, { withCredentials: true });
      setNfce(r.data);
      if (r.data.status === 'erro_autorizacao' || r.data.status === 'denegado') alert(`SEFAZ rejeitou a NFC-e:\n${r.data.mensagem_sefaz || 'erro desconhecido'}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao emitir NFC-e');
    } finally {
      setIssuing(false);
    }
  };

  const items = sale.items || [];
  const activeItems = items.filter((i) => !i.cancelled);
  const cancelledItems = items.filter((i) => i.cancelled);
  const subtotal = activeItems.reduce((s, i) => s + i.subtotal, 0);

  const fmtDate = (d) => new Date(d).toLocaleString('pt-BR');
  const fmtMoney = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
  // Exibição para etiqueta de balança modo Preço: mostra o peso calculado (valor total / preço/kg
  // cadastrado) sem alterar o valor cobrado. Ex.: R$4,42 / R$47,99 = 0,092 kg.
  const displayLine = (it) => {
    if (it.scale_price_mode && Number(it.catalog_price) > 0) {
      return {
        qty: Math.round((Number(it.subtotal) / Number(it.catalog_price)) * 1000) / 1000,
        unitPrice: Number(it.catalog_price),
      };
    }
    return { qty: Number(it.quantity), unitPrice: Number(it.unit_price) };
  };
  const paymentLabel = (p) =>
    ({ dinheiro: 'Dinheiro', cartao: 'Cartão', pix: 'PIX Loja', pix_maquininha: 'PIX Maquininha', credito: 'Crédito', debito: 'Débito', voucher: 'Voucher', cheque: 'Cheque', credito_loja: 'Crediário', a_prazo: 'A Prazo' }[p] || p);

  // Rótulo da forma de pagamento no cupom: vendas no fiado mostram "A Prazo" / "Misto (A Prazo)"
  // em vez de "Cartão" (que era o método primário usado para vendas mistas).
  const isCreditSale = !!sale.on_credit
    || sale.payment_method === 'a_prazo'
    || (Array.isArray(sale.payments) && sale.payments.some((p) => p.method === 'a_prazo'));
  // Formas discriminadas no cupom (venda com mais de uma forma de pagamento)
  const multiPayments = (Array.isArray(sale.payments) ? sale.payments : []).filter((p) => Number(p.amount) > 0);
  const displayMethod = () => {
    if (multiPayments.length > 1) return isCreditSale ? 'Misto (A Prazo)' : 'Misto';
    if (!isCreditSale) return paymentLabel(sale.payment_method);
    const hasOther = Array.isArray(sale.payments) && sale.payments.some((p) => p.method !== 'a_prazo' && Number(p.amount) > 0);
    return hasOther ? 'Misto (A Prazo)' : 'A Prazo';
  };

  const fmtKey = (k) => k ? k.replace(/(\d{4})/g, '$1 ').trim() : '';

  // Monta o HTML do cupom programaticamente, garantindo dados da empresa
  // SEMPRE no impresso (não depende do React ter renderizado o printRef).
  const buildPrintableHtml = () => {
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const c = company || {};
    const headerLines = [];
    // PRIORIDADE 1: se houver `receipt_header` customizado, usa-o LITERAL
    if (c.receipt_header && c.receipt_header.trim()) {
      const customLines = c.receipt_header.split('\n');
      // Primeira linha = título (bold). Demais = small centralizado.
      customLines.forEach((line, idx) => {
        const safe = esc(line);
        if (idx === 0) {
          headerLines.push(`<h2 class="bold center">${safe || '&nbsp;'}</h2>`);
        } else {
          headerLines.push(`<div class="center small">${safe || '&nbsp;'}</div>`);
        }
      });
    } else {
      // PRIORIDADE 2: monta automaticamente dos campos estruturados
      headerLines.push(`<h2 class="bold center">${esc(c.trade_name || c.legal_name || 'Smart Flow')}</h2>`);
      if (c.cnpj || c.ie) {
        headerLines.push(`<div class="center small">${c.cnpj ? `cnpj: ${esc(c.cnpj)}` : ''}${c.cnpj && c.ie ? '  ' : ''}${c.ie ? `IE: ${esc(c.ie)}` : ''}</div>`);
      }
      if (c.address) headerLines.push(`<div class="center small">end: ${esc(c.address)}</div>`);
      if (c.neighborhood || c.phone) {
        headerLines.push(`<div class="center small">${c.neighborhood ? `bairro: ${esc(c.neighborhood)}` : ''}${c.neighborhood && c.phone ? '  ' : ''}${c.phone ? `f: ${esc(c.phone)}` : ''}</div>`);
      }
      if (c.city || c.zip_code) {
        const cityPart = c.city ? `cidade: ${esc(c.city)}${c.state ? `-${esc(c.state)}` : ''}` : '';
        headerLines.push(`<div class="center small">${cityPart}${c.city && c.zip_code ? '  ' : ''}${c.zip_code ? `cep: ${esc(c.zip_code)}` : ''}</div>`);
      }
    }

    const nfceRejected = nfce && ['erro_autorizacao', 'denegado'].includes(nfce.status);
    const tipoCupom = nfceOk ? nfceLabel(nfce)
      : (['fiscal', 'nfe'].includes(sale.cupom_type) && !nfceRejected ? 'CUPOM FISCAL' : 'cupom nao fiscal');
    const consumidor = sale.customer_name
      ? (isCreditSale
          ? `<div class="center bold" style="margin-top:4px">VENDA A PRAZO - CLIENTE</div><div class="cust-name-big">${esc((sale.customer_name || '').toUpperCase())}</div>${sale.customer_cpf ? `<div class="small center">CPF: ${esc(sale.customer_cpf)}</div>` : ''}`
          : `<div class="small">consumidor: ${esc((sale.customer_name || '').toLowerCase().replace(/(^|\s)([\p{L}])/gu, m => m.toUpperCase()))}${sale.customer_cpf ? ` · CPF: ${esc(sale.customer_cpf)}` : ''}</div>`)
      : '';

    const itensHtml = activeItems.map((it, i) => {
      const nameCapitalized = (it.product_name || '').toLowerCase().replace(/(^|\s)([\p{L}])/gu, (m) => m.toUpperCase());
      const dl = displayLine(it);
      return `
      <tr>
        <td>
          <div class="item-name">${i + 1}. ${esc(nameCapitalized)}</div>
          <div class="item-sub">${dl.qty.toFixed(3)} x ${fmtMoney(dl.unitPrice)}</div>
        </td>
        <td class="right item-name">${fmtMoney(it.subtotal)}</td>
      </tr>`;
    }).join('');

    const cancelHtml = cancelledItems.length === 0 ? '' : `
      <hr/><div class="small bold">Itens cancelados:</div>
      <table><tbody>
        ${cancelledItems.map((it) => `
          <tr>
            <td class="line-through small">${esc(it.product_name)} (${Number(it.quantity).toFixed(3)})</td>
            <td class="right line-through small">${fmtMoney(it.subtotal)}</td>
          </tr>`).join('')}
      </tbody></table>`;

    const nfceHtml = !nfceOk ? '' : `
      <div class="center bold small" style="margin-top:4px">Consulte pela chave de acesso em:</div>
      <div class="center small">${esc(nfce.consultation_url || '')}</div>
      <div class="access-key bold">${fmtKey(nfce.access_key)}</div>
      ${nfce.qr_code_url ? `
        <div class="qr" data-qr="${esc(nfce.qr_code_url)}" style="text-align:center; margin-top:6px">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=2&data=${encodeURIComponent(nfce.qr_code_url)}" alt="QR Code NFC-e" style="width:180px; height:180px" />
        </div>
        <div class="center small" style="margin-top:2px">QR Code para consulta da NFC-e</div>
      ` : ''}
      ${nfce.protocolo ? `<div class="center small">Protocolo: ${esc(nfce.protocolo)}</div>` : ''}
      ${nfce.status === 'contingencia_pendente' ? '<div class="center bold">EMITIDA EM CONTINGENCIA OFFLINE</div><div class="center small">Pendente de autorizacao da SEFAZ</div>' : ''}
      ${nfce.ambiente !== 'producao' ? '<div class="center bold">EMITIDA EM AMBIENTE DE HOMOLOGACAO - SEM VALOR FISCAL</div>' : ''}
      <hr/>`;

    const deliveryHtml = !(sale.delivery && (sale.delivery.name || sale.delivery.address)) ? '' : `
      <div class="center bold">*** DELIVERY ***</div>
      ${sale.delivery.name ? `<div class="small bold">Cliente: ${esc(sale.delivery.name)}</div>` : ''}
      ${sale.delivery.address ? `<div class="small">End: ${esc(sale.delivery.address)}</div>` : ''}
      ${sale.delivery.neighborhood ? `<div class="small">Bairro: ${esc(sale.delivery.neighborhood)}</div>` : ''}
      ${sale.delivery.phone ? `<div class="small">Fone: ${esc(sale.delivery.phone)}</div>` : ''}
      ${sale.delivery.notes ? `<div class="small">Obs: ${esc(sale.delivery.notes)}</div>` : ''}
      <hr/>`;

    return `
      ${c.logo_base64 ? `<div style="text-align:center;margin-bottom:4px"><img src="${c.logo_base64}" alt="logo" style="width:120px;height:120px;object-fit:contain;display:inline-block" /></div>` : ''}
      ${headerLines.join('')}
      <hr/>
      <div class="center bold">${tipoCupom}</div>
      <div class="center small">venda #${esc(sale.sale_number || '-')}${nfce ? ` · NFC-e ${nfce.number}/${nfce.serie}` : ''}</div>
      <div class="center small">${esc(fmtDate(sale.created_at))}</div>
      <div class="small bold">operador: ${esc(sale.cashier_name)}</div>
      ${consumidor}
      <hr/>
      <table><tbody>${itensHtml}</tbody></table>
      ${cancelHtml}
      <hr/>
      <table><tbody>
        <tr><td>subtotal:</td><td class="right">${fmtMoney(subtotal)}</td></tr>
        ${Number(sale.discount) > 0 ? `<tr><td>desconto:</td><td class="right">- ${fmtMoney(sale.discount)}</td></tr>` : ''}
        <tr><td class="bold">total:</td><td class="right bold">${fmtMoney(sale.total)}</td></tr>
        ${multiPayments.length > 1
          ? `<tr><td class="bold">pagamento:</td><td class="right bold">${displayMethod()}</td></tr>` + multiPayments.map((p) => `<tr><td>&nbsp;&nbsp;${esc(paymentLabel(p.method))}:</td><td class="right">${fmtMoney(p.amount)}</td></tr>`).join('')
          : `<tr><td>pagamento:</td><td class="right">${displayMethod()}</td></tr>`}
        ${sale.on_credit ? `<tr><td colspan="2" class="bold center">VENDA A PRAZO</td></tr>` : ''}
      </tbody></table>
      <hr/>
      ${sale.status === 'cancelled' ? `<div class="center bold" style="color:#b91c1c">*** VENDA CANCELADA ***<div class="small">Motivo: ${esc(sale.cancelled_reason || '')}</div></div>` : ''}
      ${nfceHtml}
      ${deliveryHtml}
      ${(sale.on_credit || isCreditSale) ? `
        <div class="sign-area">
          <div class="center sign-text bold">COMPROMISSO DE COMPRA A PRAZO</div>
          <div class="sign-text">Reconheço a compra acima no valor de ${fmtMoney(sale.total)} e me comprometo a efetuar o pagamento no prazo combinado.</div>
          <div class="sign-line"></div>
          <div class="center small">Assinatura do cliente</div>
        </div>
        <hr/>
      ` : ''}
      ${c.receipt_footer ? `<div class="center small" style="margin-top:4px">${esc(c.receipt_footer)}</div>` : ''}
      <div class="center small">obrigado pela preferência!</div>
    `;
  };

  const buildFullDocument = () => {
    const html = buildPrintableHtml();
    return `
      <html><head><title>Cupom #${sale.sale_number || ''}</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        * { box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 6px; color: #000; line-height: 1.25; }
        h2,h3 { margin: 0; padding: 0; text-align: center; font-size: 12px; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: 2px 0; vertical-align: top; }
        .right { text-align: right; }
        .center { text-align: center; }
        .left { text-align: left; }
        .bold { font-weight: bold; }
        .small { font-size: 10px; }
        .line-through { text-decoration: line-through; color: #888; }
        .item-name { font-size: 11px; line-height: 1.25; font-weight: bold; }
        .item-sub { font-size: 11px; line-height: 1.2; }
        .items-text { font-size: 11px; line-height: 1.25; }
        .cust-name-big { font-size: 18px; font-weight: bold; text-align: center; text-transform: uppercase; margin: 3px 0 4px; }
        .sign-area { margin-top: 20px; }
        .sign-line { border-top: 1px solid #000; margin: 52px 6px 3px 6px; }
        .sign-text { font-size: 11px; margin-top: 6px; text-align: center; }
        .access-key { font-size: 9px; word-break: break-all; text-align: center; margin: 4px 0; }
      </style>
      </head><body>${html}</body></html>
    `;
  };

  // Reimpressão: sempre pelo Print Agent (sem diálogo do navegador)
  const handlePrint = () => {
    const paidCash = (sale.payments || []).some((p) => p.method === 'dinheiro' && Number(p.amount) > 0) || (!sale.payments?.length && sale.payment_method === 'dinheiro');
    printHtmlSilently(buildFullDocument(), { openDrawer: paidCash });
  };

  if (silent) return null; // modo silencioso: nada visível — só a impressão direta

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="receipt-modal"
    >
      <div
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Cupom #{sale.sale_number || sale.id?.substring(0, 8)}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="receipt-close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          <div ref={printRef} className="text-[11px] leading-tight" style={{ fontFamily: "'Courier New', monospace" }}>
            {/* ===== LOGO ===== */}
            {company.logo_base64 && (
              <div className="text-center mb-1">
                <img src={company.logo_base64} alt="Logo da loja" className="inline-block" style={{ width: 96, height: 96, objectFit: 'contain' }} />
              </div>
            )}
            {/* ===== CABEÇALHO ===== */}
            {company.receipt_header && company.receipt_header.trim() ? (
              // Cabeçalho customizado (texto livre de Configurações > Impressora)
              company.receipt_header.split('\n').map((line, idx) => (
                idx === 0
                  ? <h2 key={idx} className="bold center">{line || '\u00A0'}</h2>
                  : <div key={idx} className="center small">{line || '\u00A0'}</div>
              ))
            ) : (
              <>
                <h2 className="bold center">{company.trade_name || company.legal_name || 'Smart Flow'}</h2>
                {(company.cnpj || company.ie) && (
                  <div className="center small">
                    {company.cnpj && <>cnpj: {company.cnpj}</>}
                    {company.cnpj && company.ie && '  '}
                    {company.ie && <>IE: {company.ie}</>}
                  </div>
                )}
                {company.address && <div className="center small">end: {company.address}</div>}
                {(company.neighborhood || company.phone) && (
                  <div className="center small">
                    {company.neighborhood && <>bairro: {company.neighborhood}</>}
                    {company.neighborhood && company.phone && '  '}
                    {company.phone && <>f: {company.phone}</>}
                  </div>
                )}
                {(company.city || company.zip_code) && (
                  <div className="center small">
                    {company.city && <>cidade: {company.city}{company.state ? `-${company.state}` : ''}</>}
                    {company.city && company.zip_code && '  '}
                    {company.zip_code && <>cep: {company.zip_code}</>}
                  </div>
                )}
              </>
            )}
            <hr />
            <div className="center bold">
              {nfceOk ? nfceLabel(nfce) : (['fiscal', 'nfe'].includes(sale.cupom_type) && !['erro_autorizacao', 'denegado'].includes(nfce?.status) ? 'CUPOM FISCAL' : 'cupom nao fiscal')}
            </div>
            <div className="center small">
              venda #{sale.sale_number || '-'}
              {nfce ? ` · NFC-e ${nfce.number}/${nfce.serie}` : ''}
            </div>
            <div className="center small">{fmtDate(sale.created_at)}</div>
            <div className="small bold">operador: {sale.cashier_name}</div>
            {sale.customer_name && (
              isCreditSale
                ? <div className="text-center" style={{ margin: '4px 0' }} data-testid="receipt-credit-customer">
                    <div className="font-bold">VENDA A PRAZO - CLIENTE</div>
                    <div className="font-bold uppercase" style={{ fontSize: '18px', lineHeight: 1.2 }}>{sale.customer_name}</div>
                    {sale.customer_cpf ? <div className="small">CPF: {sale.customer_cpf}</div> : null}
                  </div>
                : <div className="small">consumidor: <span style={{ textTransform: 'capitalize' }}>{sale.customer_name.toLowerCase()}</span>{sale.customer_cpf ? ` · CPF: ${sale.customer_cpf}` : ''}</div>
            )}
            <hr />

            {/* ===== ITENS ===== */}
            <table>
              <tbody>
                {activeItems.map((it, i) => {
                  const dl = displayLine(it);
                  return (
                  <tr key={i}>
                    <td>
                      <div className="bold" style={{ fontSize: '11px', textTransform: 'capitalize', lineHeight: 1.25 }}>{i + 1}. {(it.product_name || '').toLowerCase()}</div>
                      <div style={{ fontSize: '11px' }}>
                        {dl.qty.toFixed(3)} x {fmtMoney(dl.unitPrice)}
                      </div>
                    </td>
                    <td className="right bold" style={{ fontSize: '11px' }}>{fmtMoney(it.subtotal)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {cancelledItems.length > 0 && (
              <>
                <hr />
                <div className="small bold">Itens cancelados:</div>
                <table>
                  <tbody>
                    {cancelledItems.map((it, i) => (
                      <tr key={i}>
                        <td className="line-through small">{it.product_name} ({Number(it.quantity).toFixed(3)})</td>
                        <td className="right line-through small">{fmtMoney(it.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <hr />
            <table>
              <tbody>
                <tr><td>subtotal:</td><td className="right">{fmtMoney(subtotal)}</td></tr>
                {Number(sale.discount) > 0 && (
                  <tr><td>desconto:</td><td className="right">- {fmtMoney(sale.discount)}</td></tr>
                )}
                <tr><td className="bold">total:</td><td className="right bold">{fmtMoney(sale.total)}</td></tr>
                <tr><td className={multiPayments.length > 1 ? 'bold' : ''}>pagamento:</td><td className={`right ${multiPayments.length > 1 ? 'bold' : ''}`}>{displayMethod()}</td></tr>
                {multiPayments.length > 1 && multiPayments.map((p, i) => (
                  <tr key={i} data-testid={`receipt-payment-${p.method}`}><td>&nbsp;&nbsp;{paymentLabel(p.method)}:</td><td className="right">{fmtMoney(p.amount)}</td></tr>
                ))}
                {sale.on_credit && (
                  <tr><td colSpan={2} className="bold center">VENDA A PRAZO</td></tr>
                )}
              </tbody>
            </table>
            <hr />

            {sale.status === 'cancelled' && (
              <div className="center bold" style={{ color: '#b91c1c' }}>
                *** VENDA CANCELADA ***
                <div className="small">Motivo: {sale.cancelled_reason}</div>
              </div>
            )}

            {/* ===== QR Code SEFAZ (NFCe) ===== */}
            {nfce && nfce.qr_code_url && (
              <>
                <div className="center bold small" style={{ marginTop: 4 }}>Consulte pela chave de acesso em:</div>
                <div className="center small">{nfce.consultation_url || 'www.dfe.uf.gov.br/nfce'}</div>
                <div className="access-key bold">{fmtKey(nfce.access_key)}</div>
                <div className="qr-box">
                  <QRCode value={nfce.qr_code_url} size={140} />
                </div>
                <div className="center small">
                  Ambiente: {nfce.ambiente === 'producao' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'} · Status: {nfce.status === 'draft_local' ? 'Rascunho Local' : nfce.status}
                </div>
                <hr />
              </>
            )}

            {/* ===== ENTREGA / DELIVERY (rodapé) ===== */}
            {sale.delivery && (sale.delivery.name || sale.delivery.address) && (
              <>
                <div className="center bold">*** DELIVERY ***</div>
                {sale.delivery.name && <div className="small bold">Cliente: {sale.delivery.name}</div>}
                {sale.delivery.address && <div className="small">End: {sale.delivery.address}</div>}
                {sale.delivery.neighborhood && <div className="small">Bairro: {sale.delivery.neighborhood}</div>}
                {sale.delivery.phone && <div className="small">Fone: {sale.delivery.phone}</div>}
                {sale.delivery.notes && <div className="small">Obs: {sale.delivery.notes}</div>}
                <hr />
              </>
            )}

            {(sale.on_credit || isCreditSale) && (
              <>
                <div style={{ marginTop: 16 }}>
                  <div className="center bold" style={{ fontSize: '12px' }}>COMPROMISSO DE COMPRA A PRAZO</div>
                  <div className="center" style={{ fontSize: '12px' }}>Reconheço a compra acima no valor de {fmtMoney(sale.total)} e me comprometo a efetuar o pagamento no prazo combinado.</div>
                  <div style={{ borderTop: '1px solid #000', margin: '52px 6px 3px 6px' }} />
                  <div className="center small">Assinatura do cliente</div>
                </div>
                <hr />
              </>
            )}

            {company.receipt_footer && (
              <div className="center small" style={{ marginTop: 4 }}>{company.receipt_footer}</div>
            )}
            <div className="center small">obrigado pela preferência!</div>
          </div>
        </div>

        <div className="flex gap-2 p-3 border-t border-slate-200">
          <Button ref={closeBtnRef} variant="outline" onClick={onClose} className="flex-1" data-testid="receipt-close">Fechar</Button>
          {['fiscal', 'nfe'].includes(sale.cupom_type) && (!nfceOk || nfce?.status === 'draft_local') && company.nfce_enabled && (
            <Button onClick={handleIssueNFCe} disabled={issuing} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="receipt-issue-nfce">
              <FileText size={16} className="mr-2" /> {issuing ? 'Transmitindo à SEFAZ...' : (nfce?.status === 'draft_local' ? 'Transmitir NFC-e à SEFAZ' : nfce ? 'Tentar NFC-e novamente' : 'Emitir NFC-e')}
            </Button>
          )}
          <Button
            ref={printBtnRef}
            onClick={handlePrint}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            data-testid="receipt-print"
            autoFocus
          >
            <Printer size={16} className="mr-2" /> Imprimir
          </Button>
        </div>
      </div>
    </div>
  );
};
